<template>
  <!-- 客户列表字段配置模态框 -->
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              列表字段配置
            </h3>
            <p class="text-sm text-gray-500 mt-1 hidden sm:block">
              自定义客户列表的字段显示和排序
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 配置说明和操作按钮 -->
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 p-4 bg-blue-50 rounded-lg hidden sm:flex">
          <div class="flex-1">
            <div class="text-sm text-blue-800 space-y-1">
              <div>• 拖拽调整字段显示顺序</div>
              <div>• 开关控制字段显示/隐藏</div>
              <div>• 设置列宽优化显示效果</div>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 ml-6">
            <button
              @click="resetConfig"
              :disabled="submitting"
              class="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              重置配置
            </button>
            <button
              @click="saveConfig"
              :disabled="!hasChanges || submitting"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {{ submitting ? '保存中...' : '保存配置' }}
            </button>
          </div>
        </div>

        <!-- 字段配置列表 -->
        <div class="space-y-3">
          <!-- 表格头部 (桌面端) -->
          <div class="hidden sm:grid grid-cols-10 gap-4 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">类型</div>
            <div class="col-span-2 text-center">列宽</div>
            <div class="col-span-2 text-center">显示</div>
          </div>

          <!-- 移动端表格头部 -->
          <div class="grid grid-cols-6 gap-2 text-sm font-medium text-gray-700 pb-2 border-b border-gray-200 sm:hidden">
            <div class="col-span-1 text-center">排序</div>
            <div class="col-span-3">字段名称</div>
            <div class="col-span-2 text-center">显示</div>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="flex flex-col items-center justify-center py-12">
            <!-- Loading Spinner -->
            <div class="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
            <div class="text-sm text-gray-600">加载中...</div>
          </div>

          <!-- 字段配置项 -->
          <draggable
            v-else
            v-model="localFieldConfigs"
            group="fieldConfigs"
            @end="handleSort"
            item-key="id"
            class="space-y-2"
          >
            <template #item="{ element: config }">
              <div>
                <!-- 桌面端布局 -->
                <div class="hidden sm:grid grid-cols-10 gap-4 items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center">
                    <div class="cursor-move text-gray-400 hover:text-gray-600">
                      <Bars3Icon class="h-5 w-5" />
                    </div>
                  </div>

                  <!-- 字段名称 -->
                  <div class="col-span-3">
                    <div class="flex items-center space-x-1">
                      <input
                        v-model="config.field_label"
                        type="text"
                        class="w-24 justify-end text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        @input="markAsChanged"
                      />
                      <span 
                        v-if="config.field_type === 'system'" 
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        系统
                      </span>
                      <span 
                        v-else 
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                      >
                        自定义
                      </span>
                    </div>
                  </div>

                  <!-- 字段类型 -->
                  <div class="col-span-2 text-center">
                    <span class="text-sm text-gray-600">
                      {{ getFieldTypeDisplay(config) }}
                    </span>
                  </div>

                  <!-- 列宽设置 -->
                  <div class="col-span-2 flex justify-center">
                    <input
                      v-model.number="config.column_width"
                      type="number"
                      min="60"
                      max="500"
                      class="w-20 text-sm text-center border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      @input="markAsChanged"
                    />
                  </div>

                  <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="inline-flex items-center">
                      <input
                        v-model="config.is_visible"
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        @change="markAsChanged"
                      />
                    </label>
                  </div>
                </div>

                <!-- 移动端布局 -->
                <div class="grid grid-cols-6 gap-2 items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors sm:hidden">
                  <!-- 拖拽手柄 -->
                  <div class="col-span-1 flex justify-center">
                    <div class="cursor-move text-gray-400 hover:text-gray-600">
                      <Bars3Icon class="h-4 w-4" />
                    </div>
                  </div>

                  <!-- 字段名称 -->
                  <div class="col-span-3">
                    <div class="space-y-1">
                      <input
                        v-model="config.field_label"
                        type="text"
                        class="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                        @input="markAsChanged"
                      />
                      <!-- 移动端隐藏系统标签 -->
                    </div>
                  </div>

                  <!-- 显示开关 -->
                  <div class="col-span-2 flex justify-center">
                    <label class="inline-flex items-center">
                      <input
                        v-model="config.is_visible"
                        type="checkbox"
                        class="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        @change="markAsChanged"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </template>
          </draggable>
        </div>

        <!-- 移动端操作按钮 -->
        <div class="flex space-x-3 mt-6 sm:hidden">
          <button
            @click="resetConfig"
            :disabled="submitting"
            class="flex-1 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            重置配置
          </button>
          <button
            @click="saveConfig"
            :disabled="!hasChanges || submitting"
            class="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {{ submitting ? '保存中...' : '保存配置' }}
          </button>
        </div>

        <!-- 底部按钮 -->
        <div class="mt-6 flex justify-end">
          <button
            @click="closeModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            关闭
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { XMarkIcon, Bars3Icon } from '@heroicons/vue/24/outline'
import { useToast } from 'vue-toastification'
import draggable from 'vuedraggable'
import { customerFieldConfigService } from '@/services/customerService'

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'success'])

// Toast
const toast = useToast()

// State
const loading = ref(false)
const submitting = ref(false)
const fieldConfigs = ref([])
const localFieldConfigs = ref([])
const originalConfigs = ref([])

// Computed
const hasChanges = computed(() => {
  if (!originalConfigs.value.length || !localFieldConfigs.value.length) {
    return false
  }
  
  return JSON.stringify(localFieldConfigs.value) !== JSON.stringify(originalConfigs.value)
})

// Methods
const loadFieldConfigs = async () => {
  try {
    loading.value = true
    const response = await customerFieldConfigService.getFieldConfigs()
    
    if (response.success) {
      fieldConfigs.value = response.results || []
      
      // 如果没有任何配置，自动初始化
      if (fieldConfigs.value.length === 0) {
        try {
          const initResponse = await customerFieldConfigService.initializeFieldConfigs()
          if (initResponse.success) {
            fieldConfigs.value = initResponse.data || []
            toast.success('字段配置已自动初始化')
          }
        } catch (initError) {
          console.error('初始化字段配置失败:', initError)
        }
      }
      
      localFieldConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
      originalConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
    }
  } catch (error) {
    console.error('加载字段配置失败:', error)
    
    // 如果加载失败，也尝试初始化
    try {
      const initResponse = await customerFieldConfigService.initializeFieldConfigs()
      if (initResponse.success) {
        fieldConfigs.value = initResponse.data || []
        localFieldConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
        originalConfigs.value = JSON.parse(JSON.stringify(fieldConfigs.value))
        toast.success('字段配置已自动初始化')
      } else {
        toast.error('加载字段配置失败')
      }
    } catch (initError) {
      console.error('初始化字段配置失败:', initError)
      toast.error('加载字段配置失败')
    }
  } finally {
    loading.value = false
  }
}

const saveConfig = async () => {
  try {
    submitting.value = true
    
    // 更新排序号
    const configsToUpdate = localFieldConfigs.value.map((config, index) => ({
      id: config.id,
      field_label: config.field_label,
      is_visible: config.is_visible,
      sort_order: index + 1,
      column_width: config.column_width || 120
    }))
    
    const response = await customerFieldConfigService.bulkUpdateFieldConfigs(configsToUpdate)
    if (response.success) {
      toast.success(response.message || '字段配置保存成功')
      await loadFieldConfigs()
      emit('success')
    }
  } catch (error) {
    console.error('保存字段配置失败:', error)
    toast.error(error.response?.data?.message || '保存字段配置失败')
  } finally {
    submitting.value = false
  }
}

const resetConfig = async () => {
  if (!confirm('确定要重置为默认配置吗？这将丢失当前的所有自定义设置。')) {
    return
  }
  
  try {
    submitting.value = true
    const response = await customerFieldConfigService.initializeFieldConfigs()
    if (response.success) {
      toast.success('配置已重置为默认设置')
      await loadFieldConfigs()
      emit('success')
    }
  } catch (error) {
    console.error('重置配置失败:', error)
    toast.error('重置配置失败')
  } finally {
    submitting.value = false
  }
}

const handleSort = () => {
  markAsChanged()
}

const markAsChanged = () => {
  // 标记为已更改，触发 hasChanges 计算属性重新计算
}

const closeModal = () => {
  emit('close')
}

const getFieldTypeDisplay = (config) => {
  if (config.field_type === 'system') {
    return '系统字段'
  } else if (config.custom_field_info) {
    const typeMap = {
      'text': '文本',
      'textarea': '多行文本',
      'number': '数字',
      'decimal': '小数',
      'date': '日期',
      'boolean': '是/否',
      'choice': '单选',
      'multiple_choice': '多选',
      'url': '网址',
      'email': '邮箱'
    }
    return typeMap[config.custom_field_info.field_type] || config.custom_field_info.field_type
  }
  return '未知'
}

// Watch for modal visibility
watch(() => props.show, (newShow) => {
  if (newShow) {
    loadFieldConfigs()
  }
})

// Load field configs on mount
onMounted(() => {
  if (props.show) {
    loadFieldConfigs()
  }
})
</script>
